import { pageNote, IResponse, SavenoteResponse } from '../interfaces';
import { PublicService, ApiService } from '@DavisDigital/davisdigitalfrontend.module';

export class BookService {

	// Cached book listing organized by school for listings.
	private bookListing: any = null;
	// Books the user has licenses for
	private availableBooks;

	// Cached resources for books by bookID.
	private resources: any = {};

	// Cached notes for pages by pageID.
	public notes: any = {};

	// Cached chapters for books by bookID.
	private chapters: any = {};

	//@ngInject
	constructor(
		private $q				: ng.IQService,
		private apiService		: ApiService,
		private publicService	: PublicService,
	) {}

	/**
	 * Gets books for listing pages.
	 *
	 * @async
	 * @return {Promise<any>} Books object organized by school
	 */
	public getBookListingData = (): ng.IPromise<any> => {

		var deferred = this.$q.defer();

		this.apiService.request('davisdigital:api.getBooksForAccount').then( result => {

			var books = result.data.data;

			if(books.length === 0){
				return deferred.resolve( books );
			}

			this.bookListing = this.sortBooksBySchool( books );
			deferred.resolve( this.bookListing );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	public getAvailableBooks = (): ng.IPromise<any> => {

		let deferred = this.$q.defer();

		this.apiService.request('davisdigital:api.getAvailableBooks').then( result => {
			this.availableBooks = result.data.data;
			deferred.resolve( this.availableBooks );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}
	
	public getBookDetails = (bookID:string, sharedToken:string = ""): ng.IPromise<any> => {
		
		let deferred = this.$q.defer();
		
		var data = {
			bookID: bookID,
			sharedToken: sharedToken
		}

		this.apiService.request('davisdigital:api.getBookDetails', data).then( result => {
			deferred.resolve( result.data.data );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	/**
	 * Gets an array of pages for a book by seaching it's pageText.
	 *
	 * @async
	 * @param {string} keyword - String to search pageText by.
	 * @param {string} bookID - Unique identifier for Book.
	 * @param {number} pageNumber - Current page to start the search from.
	 * @param {number} recordsPerPage - The number of records that will be returned.
	 * @return {Promise<any>} Collection of pages
	 */
	private getPagesForBookByKeyword = (
		keyword: string,
		bookID: string,
		pageNumber: number = 1,
		recordsPerPage: (number|string) = 10
	): ng.IPromise<any> => {

		let deferred = this.$q.defer();

		if ( 'undefined' === typeof bookID ) {
			deferred.reject('BookID cannot be null.');
			return deferred.promise;
		}

		if ( 'undefined' === typeof keyword || !keyword.length ) {
			deferred.reject('Keyword cannot be null or empty.');
			return deferred.promise;
		}

		if ( 'string' === typeof recordsPerPage ) {
			recordsPerPage = parseInt(recordsPerPage);
		}

		let data = {
			keyword: keyword,
			bookID: bookID,
			'P:Current': pageNumber,
			'P:Show': recordsPerPage,
		}

		this.apiService.request( 'davisdigital:api.getPagesForBookByKeyword', data ).then( result => {
			deferred.resolve( result );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	/**
	 * Gets spreads for books by bookID.
	 *
	 * @async
	 * @param {string} bookID - Unique identifier for a book.
	 * @return {Promise<any>} Object with details on the specified bookID.
	 */
	private getSpreadsForBook = (
		bookID: string,
		pageNumber: number = 1,
		recordsPerPage: number = 10,
		sharedToken:string = "",
	): ng.IPromise<any> => {

		let deferred = this.$q.defer();

		if ( 'undefined' === typeof bookID ) {
			deferred.reject('BookID cannot be null');
			return deferred.promise;
		}

		let data = {
			bookID: bookID,
			'P:Show': recordsPerPage,
			'P:Current': pageNumber,
		};

		if(sharedToken && sharedToken.length){
			data['sharedToken'] = sharedToken;
		}
		

		this.apiService.request( 'davisdigital:api.getPagesForBook', data ).then( result => {
			let bookPages = result.data.data;

			deferred.resolve( bookPages );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	/**
	 * Gets book resources by bookID.
	 *
	 * @async
	 * @param {string} bookID - Unique identifier for a book.
	 * @return {Promise<any>} Resources object organized by format.
	 */
	public getResourcesByBookID = ( bookID: string, resourceType: string, forceNew: boolean = false ): ng.IPromise<any> => {

		let deferred = this.$q.defer();

		if ( 'undefined' === typeof bookID ) {
			deferred.reject('BookID cannot be null');
			return deferred.promise;
		}
		
		if ( !forceNew ) {
			if ( 'undefined' !== typeof this.resources[ bookID ] && this.resources['resourceType'] === resourceType) {
				deferred.resolve( this.resources[ bookID ] );
				return deferred.promise;
			}	
		}
		
		let data = {
			bookID,
			resourceType
		};

		this.apiService.request( 'davisdigital:api.getBookResourcesForBook', data ).then( result => {
			let resources = result.data.data;

			// Sort resources by format type.
			this.resources[ bookID ] = resources;
			this.resources[ 'resourceType' ] = resourceType;

			deferred.resolve( this.resources[ bookID ] );

		}).catch( result => {

			deferred.reject( result );

		});

		return deferred.promise;
	}

	/**
	 * Gets pageNotes by page and bookID
	 *
	 * @async
	 * @param {string} bookID - Unique identifier for a book. Used to ensure permissions to the book.
	 * @param {string} pageID - Unique identifier for a page.
	 * @return {Promise<Array<pageNote>>} Array of pageNotes for the provided pageID.
	 */
	public getPageNotesForPage = ( bookID: string, pageID: string ): ng.IPromise<Array<pageNote>> => {

		let deferred = this.$q.defer<Array<pageNote>>();

		if ( 'undefined' === typeof bookID ) {
			deferred.reject('BookID cannot be null');
			return deferred.promise;
		}

		if ( 'undefined' === typeof pageID ) {
			deferred.reject('BookID cannot be null');
			return deferred.promise;
		}

		if ( 'undefined' !== typeof this.notes[ pageID ] ) {
			deferred.resolve( this.notes[ pageID ] );
			return deferred.promise;
		}

		let data = {
			bookID: bookID,
			pageID: pageID,
		};

		this.apiService.request( 'davisdigital:api.getPageNotesForPage', data ).then( result => {
			let notes = result.data.data;

			for(var i = 0; i < notes.length; i++){
				notes[i].createdDateTime = Date.parse(notes[i].createdDateTime)
				notes[i].modifiedDateTime = Date.parse(notes[i].modifiedDateTime)
			}

			this.notes[ pageID ] = notes;

			deferred.resolve( this.notes[ pageID ] );

		}).catch( result => {

			deferred.reject( result );

		});

		return deferred.promise;
	}

	/**
	 * Gets pageNotes for book
	 *
	 * @async
	 * @param {any} args - key/value pairing to get notes.
	 * @return {Promise<Array<pageNote>>}
	 */
	public getPageNotesForBook = ( args: any ): ng.IPromise<Array<pageNote>> => {

		let deferred = this.$q.defer<Array<pageNote>>();

		if ( 'undefined' === typeof args.bookID ) {
			deferred.reject('BookID cannot be null');
			return deferred.promise;
		}

		this.apiService.request( 'davisdigital:api.getPageNotesForBook', args ).then( result => {
			let notes = result.data.data;
			deferred.resolve( notes );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	/**
	 * Gets chapters by bookID.
	 *
	 * @async
	 * @param {string} bookID - Unique identifier for a book.
	 * @return {Promise<any>} Chapters object.
	 */
	public getChaptersByBookID = ( bookID: string ): ng.IPromise<any> => {

		let deferred = this.$q.defer();

		if ( 'undefined' === typeof bookID ) {
			deferred.reject('BookID cannot be null');
			return deferred.promise;
		}

		if ( 'undefined' !== typeof this.chapters[ bookID ] ) {
			deferred.resolve( this.chapters[ bookID ] );
			return deferred.promise;
		}

		let data = {
			bookID: bookID
		};

		this.apiService.request( 'davisdigital:api.getChaptersForBook', data ).then( result => {
			let chapters = result.data.data;
			this.chapters[ bookID ] = chapters;

			deferred.resolve( chapters );

		}).catch( result => {

			deferred.reject( result );

		});

		return deferred.promise;
	}

	/**
	 * Sorts an array of books by school.
	 *
	 * @param {Array<any>} books
	 * @return {any} Books sorted by school type ID
	 */
	private sortBooksBySchool = ( books: Array<any> ): any => {

		let booksBySchool = {};
		books.forEach( book => {

			let typeID = book.schoolType_typeID

			book.studentBookUrl = ( 'undefined' !== typeof book.studentEdition )
				? `/book/?bookID=${book.studentEdition.bookID}`
				: null;

			book.teacherBookUrl = ( 'undefined' !== typeof book.teacherEdition )
				? `/book/?bookID=${book.teacherEdition.bookID}`
				: null;

			// If a key doesn't exist for the school, make one.
			if ( 'undefined' === typeof booksBySchool[ typeID ] ) {
				booksBySchool[ typeID ] = {
					'schoolTypeName': book.schoolType_typeName,
					'books': []
				}
			}

			booksBySchool[ typeID ].books.push( book )

		} )

		return booksBySchool;
	}

	/**
	 * Sorts an array of resources by format.
	 *
	 * @param {Array<any>} resources
	 * @return {any} Resources sorted by resource format type name
	 */
	private sortResourcesByFormat = ( resourceTypes: Array<any> ): any => {

		if ( 'undefined' === typeof resourceTypes ) {
			return [];
		}

		let resourcesByFormat = {
			resourceTypeIDMap : {}
		};

		// Sort the resources based on format ( Video or Download ).
		resourceTypes.forEach( resourceType => {
			
			var resources = resourceType['bookResources'];
			
			resourcesByFormat['resourceTypeIDMap'] = resourceType.bookResourceTypeID
			
			resources.forEach( resource => {

				let formatType = resource.bookResourceFormatType_systemCode;
				let resourceTypeName = resourceType.type_typeName;
	
				if ( 'brftDownload' === formatType ) {
	
					// If a key doesn't exist for the format type, create one.
					if ( 'undefined' === typeof resourcesByFormat[ formatType ] ) {
						resourcesByFormat[ formatType ] = {}
					}
	
					// If it's a download resource, we want to group them by the resource type.
					// If a key for the resource type doesn't exist, create one.
					if ( 'undefined' === typeof resourcesByFormat[ formatType ][ resourceTypeName ] ) {
						resourcesByFormat[ formatType ][ resourceTypeName ] = []
					}
	
					resourcesByFormat[ formatType ][ resourceTypeName ].push( resource );
	
				} else {
	
					// If a key doesn't exist for the format type, create one.
					if ( 'undefined' === typeof resourcesByFormat[ formatType ] ) {
						resourcesByFormat[ formatType ] = []
					}
	
					resourcesByFormat[ formatType ].push( resource )
				}
			});

		} );

		return resourcesByFormat;
	}

	/**
	 * Save PageNote
	 * Either update or add a new page note depending on if a pageNoteID was provided.
	 *
	 * @param {any} data - pageNotes data formatted for the PageNote entity
	 * @return {Promise<any>} API Response depending on slatAction
	 */
	public saveNote = ( data: any ) => {

		let deferred = this.$q.defer<SavenoteResponse>();

		let slatAction = 'davisdigital:api.addPageNote';

		// Update the pageNote if an ID is defined
		if ( data.pageNoteID.length ) {
			slatAction = 'davisdigital:api.updatePageNote';
		}

		this.apiService.request( slatAction, data ).then( result => {
			deferred.resolve( result.data );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	/**
	 * Delete Note
	 *
	 * @param {string} pageNoteID - ID of the pageNote to be deleted
	 * @return {Promise<any>} successful & failure actions
	 */
	public deleteNote = ( pageNoteID: any ) => {

		let deferred = this.$q.defer<IResponse<any>>();

		if ( 'undefined' === typeof pageNoteID ) {
			deferred.reject('pageNoteID cannot be null');
			return deferred.promise;
		}

		let data = {
			pageNoteID: pageNoteID
		}

		this.apiService.request( 'davisdigital:api.deletePageNote', data ).then( result => {
			deferred.resolve( result.data );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	/**
	 * Add note to notes cache.
	 *
	 * @param {pageNote} pageNote - note to add to cache
	 * @return {boolean} if the cache for the note's pageID exists
	 */
	public addNoteToCache = ( note: pageNote ): boolean => {
		if ( 'undefined' === typeof this.notes[ note.page_pageID ] ) {
			return false;
		}

		this.notes[ note.page_pageID ].push( note );
		return true;
	}


	/**
	 * Remove note from notes cache.
	 *
	 * @param {pageNote} pageNote - note to add to cache
	 * @return {boolean} if the cache for the note's pageID exists
	 */
	public removeNoteFromCache = ( note: pageNote ): boolean => {
		if ( 'undefined' === typeof this.notes[ note.page_pageID ] ) {
			return false;
		}

		this.notes[ note.page_pageID ].forEach((cachedNote, index) => {
			if ( cachedNote.pageNoteID === note.pageNoteID ) {
				this.notes[ note.page_pageID ].splice( index, 1 );
			}
		});
		return true;
	}

	/**
	 * Page Share with User
	 *
	 * @param {formData} - emailAddress, subject, message, pageID
	 * @return {Promise}
	 */
	public pageShare = ( formData : any ): ng.IPromise<any> => {

		let deferred = this.$q.defer();

		let slatAction = 'davisdigital:api.pageShare';

		this.apiService.request( slatAction, formData).then( result => {
			deferred.resolve( result.data );
		}).catch( result => {
			deferred.reject( result );
		});

		return deferred.promise;
	}

	/**
	 * Download Resource PDF from S3
	 *
	 * @param {any} resource - BookResource
	 * @return {void}
	 */
	public downloadResourcePDF = ( resource: any ): void => {

		let url = `/?slatAction=davisdigital:api.downloadResourcePDF&bookResourceID=${resource.bookResourceID}&fileName=${resource.title}.pdf`
			url = encodeURI( url );

		let el = document.createElement('a');
		el.href = url;
		el.target = '_blank';
		document.body.appendChild( el );
		el.click();
		document.body.removeChild( el );

	}


	//display page representation for pagination
	public getPageRepresentationFromSpread = (spread, pageNumber) =>{

		var useLeftPage = pageNumber % 2 == 1

		var response = {
			'pageRepresentation':'',
			'spreadPageRepresentation':''
		};

		if( spread.leftPage != null ){
			var leftPageRepresentation = (spread.leftPage.pageRepresentation.trim().length) ? spread.leftPage.pageRepresentation : this.getPageRepresentation(spread.leftPage.pageNumber);

			response['pageRepresentation'] = (useLeftPage) ? leftPageRepresentation : '';
		}

		if( spread.rightPage != null){

			var rightPageRespresentation = (spread.rightPage.pageRepresentation.trim().length) ? spread.rightPage.pageRepresentation : this.getPageRepresentation(spread.rightPage.pageNumber);

			response['pageRepresentation'] = (!useLeftPage) ? rightPageRespresentation : response['pageRepresentation'];

			response['spreadPageRepresentation'] = leftPageRepresentation + ' - ' + rightPageRespresentation;
		} else {
			response['spreadPageRepresentation'] = leftPageRepresentation;
		}

		return response;
	}

	//determine proper page representation if not is set in the data
	public getPageRepresentation = (pageNumber) =>{

		if(pageNumber < 3){
			return 'Cover';
		}

		return pageNumber;
	}
	
	private readonly sampleCache = {};
	
	public isSampleBook = (bookCode) => {
		if ( this.sampleCache[bookCode] === undefined || this.sampleCache[bookCode] === null ) {
			let _bookCode = bookCode.toLowerCase();
			this.sampleCache[bookCode] = (_bookCode.slice(-2) === '_s' || _bookCode.slice(-7) === '_sample') || _bookCode.indexOf('_sample_') >= 0;
		}
		return this.sampleCache[bookCode];
	}
}